import _ from "lodash";
import moment from "moment";

// Shapes mirror the version snapshots returned by GET /api/project/quotation/version
// (ProjectQuotation.VersionedModel.find), i.e. versionData[0].versions[n].
export interface VersionSupplier {
  supplierId: string;
}

export interface VersionUpdatedSupplier {
  supplierId: string;
  version: number; // in a SNAPSHOT this is the snapshot's own refVersion (the reliable key)
  endDate: string | Date;
}

export interface VersionSnapshot {
  refVersion: number;
  quotationExpiryDate?: string | Date;
  suppliers?: VersionSupplier[];
  updatedSuppliers?: VersionUpdatedSupplier[];
}

export interface ResolvedSupplierExpiry {
  refVersion: number;
  expiry?: string | Date;
}

// Returns the version snapshot with the highest refVersion whose suppliers include the given
// supplier id. Matches by membership + refVersion (not array position): old versions can be pruned
// so index != refVersion, and we compare refVersion explicitly rather than trusting the array to be
// sorted ascending, so this stays correct even if the source ever returns versions out of order.
export function getLatestVersionContainingSupplier(
  supplierId: string,
  versions?: VersionSnapshot[],
): VersionSnapshot | undefined {
  if (_.isEmpty(versions)) return undefined;
  let latest: VersionSnapshot | undefined;
  for (const version of versions!) {
    if (
      version.suppliers?.some((s) => s.supplierId === supplierId) &&
      (!latest || version.refVersion > latest.refVersion)
    ) {
      latest = version;
    }
  }
  return latest;
}

// Resolves a supplier's current deadline: the latest version they were INCLUDED in, using that
// version's per-supplier extension if any (snapshot updatedSuppliers are keyed by the snapshot's
// own refVersion — the live doc's updatedSuppliers.version is an unrelated offset), else that
// version's default quotationExpiryDate. An extension that is earlier than the default is ignored
// (a deadline can't be shortened this way). Returns undefined when the supplier is on no snapshot.
// The shorten guard has a backend twin (src/helpers/supplierDeadline.js, resolveSupplierDeadline)
// used by the supplier-facing API — keep the two in sync.
export function resolveSupplierExpiry(
  supplierId: string,
  versions?: VersionSnapshot[],
): ResolvedSupplierExpiry | undefined {
  const latest = getLatestVersionContainingSupplier(supplierId, versions);
  if (!latest) return undefined;
  const latestExtension = (latest.updatedSuppliers ?? [])
    .filter((s) => s.supplierId === supplierId && s.version === latest.refVersion)
    .pop()?.endDate;
  const expiry = latestExtension && moment(latestExtension).isAfter(latest.quotationExpiryDate)
    ? latestExtension
    : latest.quotationExpiryDate;
  return { refVersion: latest.refVersion, expiry };
}
